import { UUID } from 'node:crypto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IItemOperation, ItemOperationGroup, ProductionDataService } from '@shared/services/production-data.service';
import { IScannerData, ScannerListenerService } from '@shared/services/scanner-listener.service';
import { ICompletionFlow, ITerminal } from '@shared/services/terminalAdministration.service';
import { UserService } from '@shared/services/user.service';
import { BehaviorSubject, catchError, EMPTY, Observable, take } from 'rxjs';
import { CreateActiveElementSubscription } from '../../graphql/subscriptions/on-create-active-element';
import { ItemLinkService } from './itemLinkService';
import { NotificationService } from './notification.service';
import { IScanningPrefix, ScanningPrefixService } from './scanning-prefix.service';

export interface IActiveElementResponse {
	result: IActiveElement;
}

export interface IActiveElementsResponse {
	result: IActiveElement[];
}

export interface IActiveElement {
	id: UUID;
	element: string;
	terminalId: string;
	stationNumber: number;
}

export interface IPostActiveElementRequest {
	element: string;
}

@Injectable({ providedIn: 'root' })
export class ActiveWorkflowService {
	public workFlowType: string;
	private linkPrefixes: IScanningPrefix[] = [];
	protected activeElementSubject = new BehaviorSubject<IActiveElement | null>(null);
	activeElement$: Observable<IActiveElement | null> = this.activeElementSubject.asObservable();

	protected otherStationsActiveElementsSubject = new BehaviorSubject<IActiveElement[]>([]);
	otherStationsActiveElements$: Observable<IActiveElement[]> = this.otherStationsActiveElementsSubject.asObservable();

	constructor(
		private http: HttpClient,
		private userService: UserService,
		private dataService: ProductionDataService,
		private itemLinkService: ItemLinkService,
		private notificationService: NotificationService,
		prefixService: ScanningPrefixService,
		onActiveElementCreate: CreateActiveElementSubscription,
		scannerListenerService: ScannerListenerService,
	) {
		scannerListenerService
			.ScannerDataChanged()
			.pipe()
			.subscribe((c) => this.onScan(c));
		onActiveElementCreate
			.subscribe$()
			.pipe()
			.subscribe((e) => {
				const activeElement = e.onCreateActiveElement;

				if (activeElement.terminalId !== this.dataService.terminalId) return;

				if (activeElement.stationNumber === this.dataService.stationNumber) {
					this.activeElementSubject.next(activeElement);
					return;
				}

				this.otherStationsActiveElementsSubject.next([...this.otherStationsActiveElementsSubject.value.filter((element) => element.stationNumber !== activeElement.stationNumber), activeElement]);
			});
		prefixService
			.getAll()
			.pipe(take(1))
			.subscribe((e) => {
				this.linkPrefixes = e;
			});

		this.userService.accountChanged$.subscribe(() => {
			this.activeElementSubject.next(null);
			this.otherStationsActiveElementsSubject.next([]);
		});
	}

	setActiveAlias(activeElement: string) {
		const body: IPostActiveElementRequest = {
			element: activeElement,
		};
		this.http.post<IActiveElementResponse>(`${environment.awsEnvUri}/mes/terminals/${this.dataService.terminalId}/stations/${this.dataService.stationNumber}/active`, body, { headers: this.userService.getHeaders() }).subscribe();
	}

	clearActiveAlias() {
		const body: IPostActiveElementRequest = {
			element: '',
		};
		this.http.post<IActiveElementResponse>(`${environment.awsEnvUri}/mes/terminals/${this.dataService.terminalId}/stations/${this.dataService.stationNumber}/active?completePrevious=false`, body, { headers: this.userService.getHeaders() }).subscribe();
	}

	getActiveElement(): void {
		this.http.get<IActiveElementsResponse>(`${environment.awsEnvUri}/mes/terminals/${this.dataService.terminalId}/active`, { headers: this.userService.getHeaders() }).subscribe({
			next: (res) => {
				const activeElements = res.result ?? [];
				this.activeElementSubject.next(activeElements.find((element) => element.stationNumber === this.dataService.stationNumber) ?? null);
				this.otherStationsActiveElementsSubject.next(activeElements.filter((element) => element.stationNumber !== this.dataService.stationNumber));
			},
			error: (err) => {
				console.error('Error fetching active element:', err);
				this.activeElementSubject.next(null);
				this.otherStationsActiveElementsSubject.next([]);
			},
		});
	}

	public get activeAlias(): string {
		return this.activeElement?.split('%')[0] || null;
	}

	public get activeSequenceNumber(): number {
		return this.activeElement?.split('%')[1] ? Number.parseInt(this.activeElement.split('%')[1]) : null;
	}

	public get activeElement(): string | null {
		return this.activeElementSubject.value?.element;
	}

	public isActiveOnOtherStation(alias: string, items?: { sequenceNumber: number }[]): boolean {
		return this.otherStationsActiveElementsSubject.value.some((element) => {
			const [otherAlias, otherSequenceNumber] = element.element?.split('%') ?? [];
			if (otherAlias !== alias) return false;
			if (!items || !otherSequenceNumber) return true;
			const sequenceNumber = Number.parseInt(otherSequenceNumber, 10);
			return items.some((i) => i.sequenceNumber === sequenceNumber);
		});
	}

	public onScan(scannerData: IScannerData): void {
		const setActive = (alias: string): void => {
			if (this.workFlowType === 'Item') this.setItemActive(alias);
			if (this.workFlowType === 'Group') this.setGroupActive(alias);
		};

		const isLinkScan = this.linkPrefixes.some((x) => scannerData.prefix === x.prefix || (scannerData.token ?? '').startsWith(x.prefix));

		if (isLinkScan) {
			// If the scanned item begins with a link prefix, then we need to look it up in our itemlink table
			// In case the itemlink exists, try to set that active on the terminal, if not then pair the rfid
			this.itemLinkService
				.get(scannerData.token)
				.pipe(
					take(1),
					catchError((error) => {
						// 1) The RFID does NOT exist in the ItemLinks repository (404 response).
						// 2) There is an activeElement on the terminal
						// 3) The scanned value is valid (non-blank)
						// =  Pair the activeElement to the scanned value and return (its already active)
						if (error.status === 404 && scannerData.token && this.activeElement) {
							console.log(`Item link '${scannerData.token}' does not exist, pairing instead to active element...`);
							const logPrefix = `Pairing between scanned '${scannerData.token}' and active item '${this.activeElement}'`;
							this.itemLinkService
								.pair(scannerData.token, this.activeElement)
								.pipe(take(1))
								.subscribe({
									next: (itemLink) => {
										this.notificationService.notify(['mes.administration.terminal.shared.on-scan.paired-link-header'], ['mes.administration.terminal.shared.on-scan.paired-link-text', { itemLink }], 'success', { impaired: true });
									},
									error: (error) => {
										console.error(`${logPrefix} failed: `, error);
										this.notificationService.notify(['mes.administration.terminal.shared.on-scan.pair-error-header'], ['mes.administration.terminal.shared.on-scan.pair-error-text', { rfid: scannerData.token }], 'error', { impaired: true });
									},
								});
						}
						console.error('Error while getting item link: ', error);
						return EMPTY;
					}),
				)
				.subscribe((link) => setActive(link.alias));
			return;
		}

		const prefixMap: Record<string, string> = {
			Item: 'ACS_I_',
			Group: 'ACS_G_',
		};

		if (scannerData.prefix !== prefixMap[this.workFlowType]) return;

		setActive(scannerData.token);
	}

	public setItemActive(token: string) {
		const scanned = this.getTokenParts(token);
		const { group: scannedGroup, item: scannedItemOperation } = this.findMatch(this.dataService.groups, scanned.alias, scanned.sequenceNumber);
		const currentGroup = this.dataService.currentGroup;
		// If scanned item couldnt be found, clear active element.
		// If nothing is ready on the item, clear active element.
		if (!scannedGroup || !scannedItemOperation || !scannedItemOperation.items.some((e) => e.ready)) {
			this.setActiveAlias('');
			return;
		}
		const applyItem = () => {
			this.dataService.currentGroup = scannedGroup;
			this.dataService.currentItem = scannedItemOperation;
			this.setActiveAlias(`${scannedItemOperation.alias}%${scanned.sequenceNumber}`);
		};
		// If the scanned group is a new group, set the new group in the data service and when that group has been loaded, set the item.
		if (currentGroup?.name !== scannedGroup.name) {
			this.dataService
				.onCurrentGroupChanged()
				.pipe(take(1))
				.subscribe((g) => {
					if (g.name !== scannedGroup.name) return;
					applyItem();
				});
			this.dataService.currentGroup = scannedGroup;
		} else {
			applyItem();
		}
	}

	public setGroupActive(token: string) {
		const group = this.dataService.groups?.find((group) => group.name?.toUpperCase() === (token?.toUpperCase() ?? 'DEFAULT'));
		// If scanned item couldnt be found, clear active element.
		// If there are no ready items on the group, clear active element.
		if (!group || !group.items.some((e) => e.items.some((y) => y.ready))) {
			this.setActiveAlias('');
			return;
		}
		this.dataService.currentGroup = group;
		this.dataService.currentItem = group.items[0];
		this.setActiveAlias(group.name);
	}

	public getTokenParts(token: string): { alias: string; sequenceNumber: number } {
		const [aliasPart, sequenceNumberStr] = token?.split('%');
		const alias = aliasPart?.toUpperCase();
		const sequenceNumber: number = Number.parseInt(sequenceNumberStr, 10);
		return { alias, sequenceNumber };
	}

	private findMatch(groups: ItemOperationGroup[], paramAlias: string, sequenceNumber?: number): { group: ItemOperationGroup; item: IItemOperation } {
		if (!paramAlias || !groups?.length) return { group: undefined, item: undefined };
		const inputAlias = paramAlias?.toUpperCase();
		const bySequence = sequenceNumber !== undefined && !Number.isNaN(sequenceNumber);
		for (const group of groups) {
			for (const item of group.items) {
				const alias = item.alias.toUpperCase();
				if (alias === inputAlias || (inputAlias.startsWith(alias) && inputAlias.charAt(alias.length) === ':')) {
					if (bySequence && !item.items.some((i) => i.sequenceNumber === sequenceNumber)) {
						continue;
					}
					return { group, item };
				}
			}
		}
		return { group: undefined, item: undefined };
	}

	public updateWorkFlowServiceType(terminal: ITerminal | null): void {
		if (!terminal) return;

		switch (terminal?.completionFlow) {
			case ICompletionFlow.ItemCompletionOnStartFlow:
				this.workFlowType = 'Item';
				break;
			case ICompletionFlow.GroupCompletionOnStartFlow:
				this.workFlowType = 'Group';
				break;
			default:
				this.workFlowType = '';
		}
	}
}
